import { useCallback, useEffect, useState } from "react";
import { useTypedSelector } from "../../../store/reducers/use-typed-selector";
import { TextAlignmentsEnum } from "../../../types/template-layouts";

import { ColorKeys, TextOverrides } from "../../../types/working-model";
import hexToRGB from "../../../utils/hexToRGB";
import { OptionalComponent } from "../../../utils/optional-component";
import usePrevious from "../../../utils/use-previous";
import { useReadonlyMode } from "../../admin/create/shared/use-readonly-mode";
import { EContentColorOverrideNames } from "../edit-section-colors/edit-section-colors-modal";
import EditableTextDraft from "./editable-text-draft";
import QuillEditor from "./quill-editor";
import registerQuillStyles, { IRegisterQuillStylesLists } from "./register-quill-styles";
import { replaceTextAlignAttribute } from "./replace-text-align-attribute";

interface IEditableTextDraftQuillProps {
	value: string;
	placeholder?: string;
	className?: string;
	onChange: (text: string) => void;
	style?: React.CSSProperties;
	elementName?: EContentColorOverrideNames;
	onOverrideChange?: (override: TextOverrides) => void;
	styleOverrides?: TextOverrides;
	defaultColor?: ColorKeys;
	disabled?: boolean;
	useNew?: boolean; // this overrrides the date validator
	defaultTextAlignment?: TextAlignmentsEnum;
	toolbarStyles?: React.CSSProperties;
	displayOnLoading?: boolean; // prevent editor content from momentarily disappearing after editing
	disableAnchorTargets?: boolean;
	onFocus?: () => void;
	onBlur?: () => void;
}

const EditableTextDraftQuill: React.FC<IEditableTextDraftQuillProps> = ({
	value,
	placeholder,
	className,
	onChange,
	style,
	elementName,
	onOverrideChange,
	styleOverrides,
	defaultColor,
	disabled,
	useNew = false,
	defaultTextAlignment = TextAlignmentsEnum.left,
	toolbarStyles,
	displayOnLoading = false,
	disableAnchorTargets = false,
	onFocus,
	onBlur,
}) => {
	const forceQuillRerender = useTypedSelector(state => state.CreateEventReducer.forceQuillRerender);
	const workingEvent = useTypedSelector(state => state.CreateEventReducer.workingEvent);
	const reRenderQuillCount = useTypedSelector(state => state.CreateEventReducer.reRenderQuillCount);
	const eventBundle = useTypedSelector(state => state.LiveEventReducer.eventBundle);
	const loadingEvent = useTypedSelector(state => state.CreateEventReducer.loadingEvent);

	const event = workingEvent || eventBundle;

	const [stylesHaveRegistered, setStylesHaveRegistered] = useState(false);
	const [styleLists, setStyleLists] = useState<IRegisterQuillStylesLists>();

	const isReadOnly = useReadonlyMode();

	// get additional colors from event settings to load into quill
	const getAdditionalColors = useCallback(() => {
		if (!useNew) return [];
		// fetch additional colors from event
		const baseColors: any[] = [];
		const customColors: any[] = [];
		// add theme colors (order all base colors first and custom colors second)
		const designColors = event?.settings?.design?.colors?.colors;
		for (const key in designColors) {
			const colorArr = designColors[key];
			if (colorArr?.[0]?.trim()?.length) {
				const newColor = hexToRGB(colorArr[0]);
				if (newColor) {
					if (key.includes('customColor')) {
						customColors.push(newColor);
					} else {
						baseColors.push(newColor);
					}
				}
			}
		}

		return [...baseColors, ...customColors];
		// disabling because we want reRenderQuillCount to trigger this useCallback
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [event?.settings?.design?.colors?.colors, useNew, reRenderQuillCount, loadingEvent]);

	const prevReRenderQuillCount = usePrevious(reRenderQuillCount);
	const previousRenderCount = usePrevious(getAdditionalColors());
	const arrLengthMatch = getAdditionalColors().length === previousRenderCount?.length;

	const reRenderCountMatch = prevReRenderQuillCount === reRenderQuillCount;
	const colorsArrayCountChange = arrLengthMatch || reRenderCountMatch;
	const shouldRenderQuill = useNew && arrLengthMatch && reRenderCountMatch;
	const shouldRegisterStyles = useNew && (!stylesHaveRegistered || colorsArrayCountChange);

	// register quill styles
	useEffect(() => {
		if (shouldRegisterStyles && !loadingEvent) {
			setStylesHaveRegistered(true);
			const _styleLists = registerQuillStyles({
				additionalColors: getAdditionalColors(),
			});
			setStyleLists(_styleLists);
		}
	}, [getAdditionalColors, shouldRegisterStyles, loadingEvent]);

	// if content is straight text (not html), we need to wrap it in html and give it the appropriate text alignment
	// or some quill features won't work
	const convertToHtml = useCallback(() => {
		if (useNew) {
			return replaceTextAlignAttribute({ htmlString: value, newValue: defaultTextAlignment, bypassIfNodeElement: true });
		}
		return value;
	}, [value, useNew, defaultTextAlignment]);

	// this validation check is important so quill editor doesn't crash when it tries to update due to refs
	// and so it also properly reloads content in module descriptions when changing module layouts on single language events
	// use displayOnLoading prop to prevent field from disappearing entirely on event reload
	if (useNew && (forceQuillRerender || loadingEvent)) {
		if (displayOnLoading) {
			return (<div className={className} dangerouslySetInnerHTML={{ __html: value }} />);
		}
		return null;
	}

	return !isReadOnly ? (
		<>
			<OptionalComponent display={shouldRenderQuill}>
				<div className={className}>
					{styleLists && (
						<QuillEditor
							value={convertToHtml()}
							onChange={onChange}
							disabled={disabled}
							styleLists={styleLists}
							toolbarStyles={toolbarStyles}
							disableAnchorTargets={disableAnchorTargets}
							onFocus={onFocus}
							onBlur={onBlur}
						/>
					)}
				</div>
			</OptionalComponent>

			<OptionalComponent display={!useNew}>
				<EditableTextDraft
					value={value}
					style={style}
					className={className}
					onChange={onChange}
					styleOverrides={styleOverrides}
					onOverrideChange={onOverrideChange}
					defaultColor={defaultColor}
					elementName={elementName}
					disabled={disabled}
					placeholder={placeholder}
					onFocus={onFocus}
					onBlur={onBlur}
				/>
			</OptionalComponent>
		</>
	) : (
		<div className={className} dangerouslySetInnerHTML={{ __html: value }} />
	);
};

export default EditableTextDraftQuill;
